import React from 'react';
import { Box, CircularProgress } from '@mui/material';

import EditorialPageShell from '../components/editorial/EditorialPageShell';
import PageHero from '../components/editorial/PageHero';
import EditorialSection from '../components/editorial/EditorialSection';
import EventDetailSection from '../components/events/EventDetailSection';
import CalendarEventRow from '../components/events/CalendarEventRow';

import { eventsData } from '../api/events/eventsData';
import { fetchCalendarEvents } from '../api/events/calendarEvents';

export default function Events() {
  const [events, setEvents] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  // KBT-176: events pulled from the shared Google Calendar, separate from
  // the staff-created (ticketed) events above. Empty until the client
  // finishes setting the calendar up and the backend's
  // KBT_EVENTS_CALENDAR_URL is configured — see calendarEvents.js.
  const [calendarEvents, setCalendarEvents] = React.useState([]);

  React.useEffect(() => {
    let mounted = true;
    (async () => {
      const response = await eventsData();
      if (mounted && response?.data?.data) {
        setEvents(response.data.data);
      }
      if (mounted) setLoading(false);
    })();
    return () => {
      mounted = false;
    };
  }, []);

  React.useEffect(() => {
    let mounted = true;
    (async () => {
      const upcoming = await fetchCalendarEvents();
      if (mounted) setCalendarEvents(upcoming);
    })();
    return () => {
      mounted = false;
    };
  }, []);

  const filteredEvents = events.filter((event) => !event.size);

  return (
    <EditorialPageShell title="Events" description="Upcoming events, shiurim, and gatherings at KBT.">
      <PageHero
        kicker="Events  ·  KBT"
        meta={filteredEvents.length ? `${filteredEvents.length} upcoming` : 'Always something new'}
        title="The kehilla, in"
        titleAccent="moments."
        subtitle="Siyumim, lectures, concerts, holidays — the public moments where the kehilla turns inside out. Take a look at what's ahead."
        image="/assets/images/kbt/siyum.jpg"
        height="76vh"
      />

      {loading ? (
        <EditorialSection kicker="Loading" title="Pulling the calendar…" align="center">
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress sx={{ color: 'var(--navy-500)' }} />
          </Box>
        </EditorialSection>
      ) : filteredEvents.length === 0 ? (
        <EditorialSection
          kicker="Quiet on the calendar"
          title="No upcoming events"
          titleAccent="just yet."
          intro="Check back soon — or write to us at office@kbtla.org and we'll tell you what's brewing."
          align="center"
        />
      ) : (
        <EditorialSection
          kicker="Upcoming"
          title="What's ahead at"
          titleAccent="KBT."
          intro="Browse the next gatherings. Tap into any one to see the details and reserve."
        >
          <Box>
            {filteredEvents.map((event, index) => (
              <EventDetailSection
                key={event.id ?? index}
                index={index}
                eventId={event.id}
                time={event?.eventTime}
                date={event?.eventDate}
                name={event?.eventTitle}
                image={event?.image}
                detail={event?.description}
              />
            ))}
          </Box>
        </EditorialSection>
      )}

      {calendarEvents.length > 0 && (
        <EditorialSection
          background="var(--paper-warm)"
          kicker="From the calendar"
          title="Also on the"
          titleAccent="shul calendar."
          intro="Shiurim, gatherings, and other happenings the kehilla keeps on our shared calendar."
        >
          <Box sx={{ maxWidth: 760, mx: 'auto' }}>
            {calendarEvents.map((event) => (
              <CalendarEventRow key={event.id} event={event} />
            ))}
          </Box>
        </EditorialSection>
      )}
    </EditorialPageShell>
  );
}
